import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { FerrySegmentRouteInfo } from '@apps/main/new-desktop/modules/ferryDetailsModal';

import type {
  CarrierStaticInfo,
  PortStaticInfo,
  TransportStaticInfo,
  TransportVehicle,
} from '@libs/trip-planner-interfaces';
import { TransportVehicleType } from '@libs/trip-planner-interfaces';

const { t } = i18n.global;

export function getAirportStaticInfo(
  iata: string,
  staticInfo: TransportStaticInfo | undefined
): PortStaticInfo | undefined {
  if (!staticInfo?.ports) {
    return;
  }
  return staticInfo.ports[iata];
}

export function getCarrierInfo(
  carrierCode: string,
  staticInfo: TransportStaticInfo | undefined
): CarrierStaticInfo | undefined {
  if (!staticInfo?.carriers) {
    return;
  }
  return staticInfo.carriers[carrierCode];
}

export function getVehicleInfo(
  ferryVehicleTypes: TransportVehicle[] | undefined,
  vehicleTypes: TransportVehicleType[] | undefined
): string[] {
  const vehicleMap = new Map();
  if (!ferryVehicleTypes) {
    return [];
  }
  ferryVehicleTypes.forEach((vehicleType) => {
    vehicleMap.set(vehicleType.id, vehicleType.description);
  });
  if (vehicleTypes?.length === 0) {
    return [vehicleMap.get(TransportVehicleType.None)];
  }

  const vehicles = vehicleTypes?.map((vehicle) => vehicleMap.get(vehicle));
  const groupedVehicles = new Map();
  vehicles?.forEach((vehicle) => {
    if (groupedVehicles.has(vehicle)) {
      groupedVehicles.set(vehicle, groupedVehicles.get(vehicle) + 1);
    } else {
      groupedVehicles.set(vehicle, 1);
    }
  });

  return Array.from(
    groupedVehicles,
    ([description, amount]) => `${amount}x ${description}`
  );
}

export function getCarrierAndTransportationName(
  carrierName: string | undefined,
  transportationName: string | undefined
): string | undefined {
  if (!carrierName && !transportationName) {
    return;
  }
  const separator = carrierName && transportationName ? ', ' : '';

  return `${carrierName ?? ''}${separator}${transportationName ?? ''}`;
}

export function getRouteInfo(
  vehicleInfo: string[] | undefined,
  ticketType: string | undefined,
  isCabinMandatory: boolean | undefined
): FerrySegmentRouteInfo[] {
  let ticketInfo = ticketType?.toLocaleLowerCase() ?? 'Economy';
  if (ticketType && ticketType.toLocaleLowerCase() === 'economy') {
    ticketInfo += ` (${t('planner.non_refundable').toLocaleLowerCase()})`;
  }
  const routeInfo = [
    {
      title: `${t('planner.vehicle')}: `,
      content: vehicleInfo,
    },
    {
      title: `${t('planner.ticket_type')}: `,
      content: ticketInfo.toUpperCase(),
    },
  ];
  if (isCabinMandatory) {
    routeInfo.push({
      title: `${t('planner.cabin_seat')}: `,
      content: `${t('planner.included')} (${t(
        'planner.can_be_changed_in_later_step'
      ).toLocaleLowerCase()}) `,
    });
  }
  return routeInfo;
}
